<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import { IMember } from '@/interfaces/IMember';
  import { Globals } from '@/modules/Globals';
  import RestaurantsService from '@/api/services/restaurants.service';
  import StatsService from '@/api/services/stats.service';
  import { getTopLevelDomain } from '@/utils/domain.utils';
  import { getEffectiveTier, isPlanExpired } from '@/utils/billing-entitlements.utils';

  const props = defineProps<{
    role: IMember;
  }>();

  const router = useRouter();
  const restaurantsService = new RestaurantsService();
  const statsService = new StatsService();
  const uploadsPath = Globals.uploadsPath;
  const domain = getTopLevelDomain();

  // State
  const stats = ref({
    todayViews: 0,
    todayOrders: 0,
    loading: true,
  });
  const statsLoaded = ref(false);

  // Computed
  const restaurant = computed(() => props.role.restaurant);
  const effectiveTier = computed(() => getEffectiveTier(restaurant.value));
  const planExpired = computed(() => isPlanExpired(restaurant.value?.planExpiresAt));

  const restaurantUrl = computed(() => {
    return `https://${domain}/${restaurant.value.stringId}`;
  });

  const planInfo = computed(() => {
    if (planExpired.value) {
      return { label: 'Vencido', color: 'red', textColor: 'white' };
    }

    if (effectiveTier.value === 0) {
      return { label: 'Lite', color: 'green', textColor: 'white' };
    } else if (effectiveTier.value === 1) {
      return { label: 'Plus', color: 'orange', textColor: 'white' };
    } else {
      const plan = Globals.plans.find(
        (p) =>
          p.tier === effectiveTier.value &&
          ((!restaurant.value.subTier && !p.subTier) ||
            p.subTier === restaurant.value.subTier)
      );
      return {
        label: plan?.title || 'Pro',
        color: '#ff6f00',
        textColor: 'white',
      };
    }
  });

  /*const isActive = computed(
    () => statsLoaded.value && stats.value.itemCount > 0
  );*/

  const bannerStyle = computed(() => {
    if (restaurant.value.banner) {
      return {
        backgroundImage: `url(${uploadsPath}/${restaurant.value.banner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return {
      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    };
  });

  const lastActivity = computed(() => {
    const date = new Date(
      restaurant.value.updatedAt || restaurant.value.createdAt
    );
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
    return `Hace ${Math.floor(diffDays / 30)} meses`;
  });

  // Methods
  /*const loadStats = async () => { // DISABLE FOR NOW as we need a branch to be selected to load stats
    try {
      // Load today's views and orders if active
      if (effectiveTier.value > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        try {
          const [viewsData, ordersData] = await Promise.all([
            statsService.getViewsStats(restaurant.value.id, null, today),
            statsService.getOrdersStats(restaurant.value.id, null, today),
          ]);

          // Sum views from all sources
          if (viewsData?.groups) {
            stats.value.todayViews = viewsData.groups.reduce((total, group) => {
              return (
                total +
                (group.stats?.reduce((sum, stat) => sum + stat.value, 0) || 0)
              );
            }, 0);
          }

          // Get order count
          if (ordersData?.groups) {
            const countGroup = ordersData.groups.find((g) => g.id === 'count');
            stats.value.todayOrders =
              countGroup?.stats?.reduce((sum, stat) => sum + stat.value, 0) ||
              0;
          }
        } catch (error) {
          console.error('Error loading daily stats:', error);
        }
      }
    } catch (error) {
      console.error('Error loading restaurant stats:', error);
    } finally {
      stats.value.loading = false;
      statsLoaded.value = true;
    }
  };*/

  const navigateToDashboard = () => {
    router.push(`/${restaurant.value.stringId}/dashboard`);
  };

  const navigateToSettings = () => {
    router.push(`/${restaurant.value.stringId}/settings`);
  };

  // Lifecycle
  onMounted(() => {
    //loadStats();
  });
</script>

<template>
  <v-card class="restaurant-card" @click="navigateToDashboard" hover>
    <!-- Banner Section -->
    <div class="card-banner" :style="bannerStyle">
      <div class="banner-overlay">
        <div class="banner-content">
          <div class="restaurant-logo">
            <v-img
              v-if="restaurant.logo"
              :src="`${uploadsPath}/${restaurant.logo}`"
              :alt="restaurant.name"
              cover
            />
            <v-icon v-else class="logo-placeholder">mdi-store</v-icon>
          </div>

          <div class="card-actions">
            <v-chip
              :color="planInfo.color"
              :text-color="planInfo.textColor"
              label
              small
            >
              {{ planInfo.label }}
            </v-chip>
            <v-btn
              icon
              small
              dark
              @click.stop="navigateToSettings"
              title="Configuración"
            >
              <v-icon small>mdi-cog</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-card-text class="card-body">
      <h3 class="restaurant-name">{{ restaurant.name }}</h3>
      <p class="restaurant-url">{{ domain }}/{{ restaurant.stringId }}</p>

      <!-- <div class="stats-row">
        <v-skeleton-loader
          v-if="stats.loading"
          type="text"
          class="stats-skeleton"
        />
        <template v-else>
          <div class="stat">
            <v-icon small class="stat-icon">mdi-menu</v-icon>
            <span class="stat-value">{{ stats.categoryCount }}</span>
            <span class="stat-label">Categorías</span>
          </div>
          <div class="stat">
            <v-icon small class="stat-icon">mdi-silverware</v-icon>
            <span class="stat-value">{{ stats.itemCount }}</span>
            <span class="stat-label">Productos</span>
          </div>
        </template>
      </div> -->

      <!-- <div v-if="effectiveTier > 0" class="activity-row">
        <div class="activity-stat">
          <v-icon small>mdi-eye</v-icon>
          <span>{{ stats.todayViews }} vistas hoy</span>
        </div>
        <div class="activity-stat">
          <v-icon small>mdi-receipt</v-icon>
          <span>{{ stats.todayOrders }} pedidos hoy</span>
        </div>
      </div> -->

      <div class="card-footer">
        <!-- <v-chip
          class="status-chip"
          :color="isActive ? 'green' : 'grey'"
          text-color="white"
          label
          x-small
        >
          <v-icon x-small left>{{
            isActive ? 'mdi-check-circle' : 'mdi-alert-circle'
          }}</v-icon>
          {{ isActive ? 'Activa' : 'Sin productos' }}
        </v-chip> -->
        <v-spacer />
        <span class="last-activity">{{ lastActivity }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .restaurant-card {
    @apply tw-relative tw-cursor-pointer tw-transition-all tw-duration-200;
    @apply tw-h-full tw-flex tw-flex-col;
    @apply tw-rounded-2xl tw-overflow-hidden;
    @apply tw-shadow-sm hover:tw-shadow-md #{!important}; // DO NOT FIX THIS LINTER ERROR

    .card-banner {
      @apply tw-relative tw-h-32 tw-w-full;
      @apply tw-bg-gradient-to-br tw-from-orange-400 tw-to-amber-500;

      .banner-overlay {
        @apply tw-absolute tw-inset-0;
        @apply tw-bg-gradient-to-t tw-from-black/50 tw-to-transparent;

        .banner-content {
          @apply tw-absolute tw-inset-x-0 tw-bottom-0 tw-p-4;
          @apply tw-flex tw-items-end tw-justify-between;

          .restaurant-logo {
            @apply tw-w-14 tw-h-14 tw-rounded-xl tw-overflow-hidden;
            @apply tw-bg-white tw-shadow-md;
            @apply tw-flex tw-items-center tw-justify-center;

            .logo-placeholder {
              @apply tw-text-2xl tw-text-neutral-400;
            }
          }

          .card-actions {
            @apply tw-flex tw-items-center tw-gap-2;
          }
        }
      }
    }

    .card-body {
      @apply tw-flex-1 tw-flex tw-flex-col tw-p-4;

      .restaurant-name {
        @apply tw-text-base tw-font-medium tw-text-neutral-800 tw-mb-1;
      }

      .restaurant-url {
        @apply tw-text-xs tw-text-neutral-500 tw-mb-3;
        @apply tw-truncate;
      }

      .stats-row {
        @apply tw-flex tw-gap-4 tw-mb-3;

        .stats-skeleton {
          @apply tw-w-full;
        }

        .stat {
          @apply tw-flex tw-items-center tw-gap-1;

          .stat-icon {
            @apply tw-text-neutral-400 tw-text-sm;
          }

          .stat-value {
            @apply tw-font-medium tw-text-neutral-700 tw-text-sm;
          }

          .stat-label {
            @apply tw-text-xs tw-text-neutral-500;
          }
        }
      }

      .activity-row {
        @apply tw-flex tw-flex-wrap tw-gap-3 tw-mb-3;
        @apply tw-text-xs tw-text-neutral-600;

        .activity-stat {
          @apply tw-flex tw-items-center tw-gap-1;

          v-icon {
            @apply tw-text-xs;
          }
        }
      }

      .card-footer {
        @apply tw-flex tw-items-center tw-justify-between;
        @apply tw-mt-auto tw-pt-2 tw-border-t tw-border-neutral-100;

        .status-chip {
          @apply tw-font-light tw-text-xs;
        }

        .last-activity {
          @apply tw-text-xs tw-text-neutral-400;
        }
      }
    }
  }
</style>
